import classNames from 'classnames';
import { useState } from 'react';
import YouTube, {
  YouTubeEvent,
  YouTubePlayer,
  YouTubeProps,
} from 'react-youtube';
import Icon from '@/components/atoms/icon/icon';
import Image from '@/components/atoms/image/image';
import styles from './youtube.module.scss';

export interface YoutubeProps {
  /** The aspect ratio to use for the video */
  aspectRatio?: Content.Properties.AspectRatio;
  /** Indicates whether the video should play automatically */
  autoPlay?: boolean;
  /** The class name to apply */
  className?: string;
  /** Indicates whether the video should show controls */
  controls?: boolean;
  /** Indicates whether to loop the video */
  loop?: boolean;
  /** Indicates the position of the play button */
  playButtonPosition?: 'center' | 'corner';
  /** The image to use as a thumbnail */
  thumbnail?: Content.Properties.Image | null;
  /** The ID of the youtube video */
  videoId?: string;
}

const Youtube = ({
  aspectRatio = 'sixteenByNine',
  autoPlay = false,
  className,
  controls,
  loop,
  playButtonPosition = 'corner',
  thumbnail,
  videoId,
}: YoutubeProps) => {
  const [isBuffering, setIsBuffering] = useState(false);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [youtubePlayer, setYoutubePlayer] = useState<YouTubePlayer>(null);

  const handleReady: YouTubeProps['onReady'] = (event: YouTubeEvent) => {
    setYoutubePlayer(event.target);
  };

  const handlePlayVideo = () => {
    if (!youtubePlayer) return;

    youtubePlayer.playVideo();
    setIsPlaying(true);
  };

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.hasThumbnail]: thumbnail,
        [styles.isBuffering]: isBuffering,
        [styles.isPlaying]: isPlaying,
        [styles.isFourByThree]: aspectRatio === 'fourByThree',
        [styles.isNineBySixteen]: aspectRatio === 'nineBySixteen',
        [styles.isOneByOne]: aspectRatio === 'oneByOne',
        [styles.isSixteenByNine]: aspectRatio === 'sixteenByNine',
        [styles.isThreeByFour]: aspectRatio === 'threeByFour',
      })}
    >
      {thumbnail?.url && (
        <button className={styles.thumbnail} onClick={handlePlayVideo}>
          <Image
            alt={thumbnail.altText}
            aspectRatio={aspectRatio}
            className={styles.thumbnailImage}
            src={thumbnail.url}
            srcSet={{
              desktop: {
                width: 1061,
              },
              laptop: {
                width: 896,
              },
              tablet: {
                width: 672,
              },
            }}
            unoptimized
            width={311}
          />

          <div
            className={classNames(styles.thumbnailButton, {
              [styles.thumbnailButtonCenter]: playButtonPosition === 'center',
              [styles.thumbnailButtonCorner]: playButtonPosition === 'corner',
            })}
          >
            <Icon name="Play" size={16} />
          </div>
        </button>
      )}

      {videoId && (
        <YouTube
          className={styles.video}
          onReady={handleReady}
          onStateChange={(event: YouTubeEvent) => {
            if (event.data === 0) {
              setIsPlaying(false);
            } else if (event.data === 1) {
              setIsBuffering(false);
              setIsPlaying(true);
            } else if (event.data === 3) {
              setIsBuffering(true);
            }
          }}
          opts={{
            height: '100%',
            playerVars: {
              autoplay: autoPlay ? 1 : 0,
              controls: controls ? 1 : 0,
              loop: loop ? 1 : 0,
              mute: autoPlay ? 1 : 0,
              playlist: videoId,
            },
            width: '100%',
          }}
          videoId={videoId}
        />
      )}
    </div>
  );
};

Youtube.displayName = 'Youtube';

export default Youtube;
